import { Marker as MarkerType, MarkerClusterSummary } from '@citiguide/shared';
import { useLanguageStore } from '../../store/languageStore';
import { useMarkerStore } from '../../store/markerStore';
import { useMap } from 'react-leaflet';
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import { contentService } from '../../services/contentService';
import { useQueryClient } from '@tanstack/react-query';
import { accountService } from '../../services/accountService';
import { mediaService } from '../../services/mediaService';
import { resolveMediaUrl } from '../../utils/mediaResolver';
import { createLogger } from '../../utils/logger';
import { getMarkerTitle } from '../../utils/markerTitle';

const log = createLogger('MarkerCluster');

function categoryToCssColor(nameOrId: string): string {
  const s = (nameOrId || '').toLowerCase();
  if (/dine|food|eat|restaurant|cafe|bar|drink|bistro|tavern/.test(s)) return 'var(--cat-dining)';
  if (/cultur|art|museum|galleri|theatre|theater|heritage|monument/.test(s)) return 'var(--cat-culture)';
  if (/nature|park|garden|forest|outdoor|trail|beach|green/.test(s)) return 'var(--cat-nature)';
  if (/shop|store|market|mall|boutique|retail/.test(s)) return 'var(--cat-shopping)';
  if (/night|club|disco|lounge|pub|entertainment/.test(s)) return 'var(--cat-nightlife)';
  if (/transit|transport|bus|train|metro|airport|station/.test(s)) return 'var(--cat-transit)';
  if (/stay|hotel|hostel|accommodat|lodg/.test(s)) return 'var(--cat-stay)';
  if (/service|info|office|health|hospital|police|admin/.test(s)) return 'var(--cat-services)';
  return 'var(--brand-primary)';
}

function categoryToIconPaths(nameOrId: string): string {
  const s = (nameOrId || '').toLowerCase();
  // utensils
  if (/dine|food|eat|restaurant|cafe|bar|drink|bistro|tavern/.test(s))
    return '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><line x1="7" y1="2" x2="7" y2="22"/><path d="M21 15V2a5 5 0 0 0-5 14v3h5z"/>';
  // landmark
  if (/cultur|art|museum|galleri|theatre|theater|heritage|monument/.test(s))
    return '<line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>';
  // leaf (nature)
  if (/nature|park|garden|forest|outdoor|trail|beach|green/.test(s))
    return '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>';
  // shopping-bag
  if (/shop|store|market|mall|boutique|retail/.test(s))
    return '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>';
  // wine
  if (/night|club|disco|lounge|pub|entertainment/.test(s))
    return '<path d="M8 22h8"/><path d="M7 10h10"/><path d="M12 15v7"/><path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H7c-1.5 4-2 6-2 8a5 5 0 0 0 5 5z"/>';
  // navigation (transit)
  if (/transit|transport|bus|train|metro|airport|station/.test(s))
    return '<polygon points="3 11 22 2 13 21 11 13 3 11"/>';
  // bed-double
  if (/stay|hotel|hostel|accommodat|lodg/.test(s))
    return '<path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/><path d="M12 10v4"/><path d="M2 18h20"/>';
  // info
  if (/service|info|office|health|hospital|police|admin/.test(s))
    return '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>';
  // map-pin dot (default)
  return '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>';
}

function createPinIcon(categoryName = ''): L.DivIcon {
  const color = categoryToCssColor(categoryName);
  const paths = categoryToIconPaths(categoryName);
  const html = `<div style="width:28px;height:28px;border-radius:50% 50% 50% 3px;transform:rotate(45deg);background:${color};border:2px solid rgba(255,255,255,0.9);box-shadow:0 2px 8px rgba(0,0,0,.28);display:flex;align-items:center;justify-content:center;"><svg style="transform:rotate(-45deg);flex-shrink:0;" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${paths}</svg></div>`;
  return L.divIcon({
    html,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [8, 28],
    popupAnchor: [6, -28],
  });
}

interface MarkerClusterProps {
  markers: MarkerType[];
  clusters?: MarkerClusterSummary[];
  isEditor?: boolean;
}

export default function MarkerCluster({ markers, clusters, isEditor = false }: MarkerClusterProps) {
  const map = useMap();
  const { currentLanguage } = useLanguageStore();
  const { setSelectedMarker } = useMarkerStore();
  const queryClient = useQueryClient();
  const [accountLanguages, setAccountLanguages] = useState<string[]>([]);
  
  // Track which popup is currently open to preserve state during updates
  const openPopupMarkerIdRef = useRef<string | null>(null);
  const markersLayerRef = useRef<any>(null);
  const isEditingRef = useRef<boolean>(false);
  const previousMarkersRef = useRef<MarkerType[]>([]);
  const isUploadingImageRef = useRef<boolean>(false);
  const imageUploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load account languages for editor mode
  useEffect(() => {
    if (isEditor) {
      const loadAccountLanguages = async () => {
        try {
          const accountInfo = await accountService.getAccountInfo();
          setAccountLanguages(accountInfo.settings.supportedLanguages || []);
        } catch (error) {
          log.error('Failed to load account languages:', error);
        }
      };
      loadAccountLanguages();
    }
  }, [isEditor]);

  // Server-side cluster rendering
  const serverClusterLayerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!map) return;

    // Clean up previous server cluster layer
    if (serverClusterLayerRef.current) {
      map.removeLayer(serverClusterLayerRef.current);
      serverClusterLayerRef.current = null;
    }

    if (!clusters || clusters.length === 0) return;

    const clusterLayer = L.layerGroup();

    clusters.forEach((cluster) => {
      const clusterIcon = L.divIcon({
        html: `<div class="server-cluster-marker"><span>${cluster.count}</span></div>`,
        className: 'server-cluster-icon',
        iconSize: L.point(40, 40),
      });

      const clusterMarker = L.marker([cluster.center[1], cluster.center[0]], {
        icon: clusterIcon,
      });

      clusterMarker.on('click', () => {
        const bounds = L.latLngBounds(
          [cluster.bounds.south, cluster.bounds.west],
          [cluster.bounds.north, cluster.bounds.east],
        );
        map.fitBounds(bounds);
      });

      clusterLayer.addLayer(clusterMarker);
    });

    clusterLayer.addTo(map);
    serverClusterLayerRef.current = clusterLayer;

    return () => {
      if (serverClusterLayerRef.current) {
        map.removeLayer(serverClusterLayerRef.current);
        serverClusterLayerRef.current = null;
      }
    };
  }, [map, clusters]);

  useEffect(() => {
    if (!map) return;

    // If we're currently editing, check if only content changed (not structure)
    // Compare markers by ID and location only, not content
    const hasStructuralChanges = 
      previousMarkersRef.current.length !== markers.length ||
      markers.some((m) => {
        const prev = previousMarkersRef.current.find(p => p.markerId === m.markerId);
        if (!prev) return true; // New marker
        // Check if location changed
        const prevCoords = JSON.stringify(prev.location?.coordinates);
        const newCoords = JSON.stringify(m.location?.coordinates);
        if (prevCoords !== newCoords) return true; // Location changed
        // Check if marker was deleted
        return false;
      }) ||
      previousMarkersRef.current.some((prev) => {
        return !markers.find(m => m.markerId === prev.markerId);
      });


    // Store the old layer for cleanup - capture current ref value
    const oldLayer = markersLayerRef.current;
    const currentLayerRef = markersLayerRef; // Capture ref itself for cleanup check
    
    // If no structural changes and we have an existing layer, reuse it to prevent visual flicker
    // This applies both during editing and normal operation to prevent markers from disappearing
    if (!hasStructuralChanges && markersLayerRef.current) {
      previousMarkersRef.current = markers;
      // Don't recreate - return cleanup that checks if layer is still current
      return () => {
        // If the layer we're about to remove is still the current layer, don't remove it
        // (we're reusing it, so it should stay)
        if (oldLayer && currentLayerRef.current === oldLayer) {
          return; // Don't remove - we're keeping it
        }
        // Layer was replaced, so remove the old one
        if (oldLayer) {
          map.removeLayer(oldLayer);
        }
      };
    }

    previousMarkersRef.current = markers;

    // Store which popup is currently open before recreating markers
    // Use the ref value if it's already set (from popupopen event), otherwise check map
    if (!openPopupMarkerIdRef.current) {
      const openPopup = (map as any)._popup;
      if (openPopup && openPopup._source) {
        const marker = openPopup._source;
        // Check for markerId property we set, or fall back to title
        if ((marker as any).markerId) {
          openPopupMarkerIdRef.current = (marker as any).markerId;
        } else if (marker.options && marker.options.title) {
          openPopupMarkerIdRef.current = marker.options.title;
        }
      }
    }

    // Remove old layer if it exists and is different from what we're about to create
    if (markersLayerRef.current) {
      map.removeLayer(markersLayerRef.current);
    }

    const markersLayer = (L as any).markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });
    
    markersLayerRef.current = markersLayer;

    markers.forEach((marker) => {
      if (!marker.location?.coordinates || marker.location.coordinates.length !== 2) {
        return;
      }

      const [lng, lat] = marker.location.coordinates;
      const leafletMarker = L.marker([lat, lng], {
        title: marker.markerId,
        icon: createPinIcon(marker.categories?.[0] || ''),
      });

      leafletMarker.on('click', () => {
        setSelectedMarker(marker.markerId);
      });

      // Create simple popup with marker info
      const popupContent = document.createElement('div');
      popupContent.className = 'marker-popup-container';
      
      // Language flags mapping
      const languageFlags: Record<string, string> = {
        'en': '🇬🇧',
        'fi': '🇫🇮',
        'se': '🇸🇪',
        'es': '🇪🇸',
        'fr': '🇫🇷',
        'de': '🇩🇪',
        'it': '🇮🇹',
        'pt': '🇵🇹',
        'zh': '🇨🇳',
        'ja': '🇯🇵',
        'ru': '🇷🇺',
        'ar': '🇸🇦'
      };
      
      let popupHTML = '';
      
      if (isEditor) {
        // Editor mode: Show tabbed interface with all languages
        const markerIdDivId = `marker-id-${marker.markerId.replace(/[^a-zA-Z0-9]/g, '-')}`;
        const popupId = `popup-${marker.markerId.replace(/[^a-zA-Z0-9]/g, '-')}`;
        
        // Marker ID header with copy icon
        popupHTML = `<div id="${markerIdDivId}" style="font-size: var(--text-2xs); font-style: italic; color: var(--text-muted); padding: 4px 8px; background: rgba(255,255,255,0.9); border-radius: 0 0 var(--radius-sm) 0; display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
          <span>ID: ${marker.markerId}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="cursor: pointer; color: var(--text-muted); flex-shrink: 0;" class="copy-icon" data-marker-id="${marker.markerId}">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </div>`;
        
        // Get all languages - use account languages if available, otherwise use languages with content
        const contentMap = marker.content || {};
        const languages = accountLanguages.length > 0 
          ? accountLanguages 
          : Object.keys(contentMap).filter(lang => contentMap[lang]);

        // Always show tabs container with all configured languages
        if (languages.length > 0) {
          popupHTML += `<div style="border-bottom: 1px solid var(--border-default); padding: 0 8px; overflow: visible;">
            <div style="display: flex; gap: 4px; overflow-x: auto; overflow-y: visible; padding: 2px 0;">
              ${languages.map((lang, index) => `
                <button class="popup-tab-btn" data-popup="${popupId}" data-lang="${lang}" style="
                  border: none;
                  background: ${index === 0 ? 'var(--surface-page)' : 'transparent'};
                  border-bottom: 2px solid ${index === 0 ? 'var(--brand-primary)' : 'transparent'};
                  cursor: pointer;
                  font-size: 22px;
                  line-height: 1.2;
                  white-space: nowrap;
                  transition: all 0.2s;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  overflow: visible;
                ">${languageFlags[lang] || lang}</button>
              `).join('')}
            </div>
          </div>`;

          // Content tabs - show all configured languages
          popupHTML += `<div class="popup-content-tabs" id="${popupId}">`;
          languages.forEach((lang, index) => {
            const content = contentMap[lang] || {};
            const contentId = `${popupId}-${lang}`;
            const ef = (val: string | undefined) => val ? '' : 'color: var(--text-subtle); font-style: italic;';
            const mkField = (label: string, val: string | undefined, dataField: string) =>
              `<div style="margin-bottom: 8px;">
                <strong style="font-size: var(--text-xs); color: var(--text-heading);">${label}:</strong>
                <div class="editable-content" data-marker-id="${marker.markerId}" data-lang="${lang}" data-field="${dataField}" data-content-id="${contentId}-${dataField}" style="font-size: var(--text-sm); color: var(--text-heading); margin-top: 2px; padding: var(--space-1) 6px; border-radius: var(--radius-sm); cursor: text; min-height: 20px; ${ef(val)}">${val || `(click to add ${label.toLowerCase()})`}</div>
              </div>`;
            popupHTML += `<div class="popup-tab-content" data-lang="${lang}" style="display: ${index === 0 ? 'block' : 'none'}; padding: 12px 8px;">
              <div style="font-size: var(--text-2xs); color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">${lang.toUpperCase()}</div>
              ${mkField('Title', content.title, 'title')}
              ${mkField('Summary', content.summary, 'summary')}
              ${mkField('Description', content.description, 'description')}
              ${mkField('Plus Info', content.plusInfo, 'plusinfo')}
              ${content.youtube ? mkField('YouTube', content.youtube, 'youtube') : ''}
            </div>`;
          });
          popupHTML += `</div>`;
          
          // Images section - show all images for this marker across all languages
          const allImages: Array<{ imageId: string; language: string; url?: string }> = [];
          languages.forEach((lang) => {
            const content = contentMap[lang] || {};
            // Support both single image (backward compatibility) and images array
            if (content.images && Array.isArray(content.images)) {
              content.images.forEach((imageId: string) => {
                allImages.push({ imageId, language: lang });
              });
            } else if (content.image) {
              // Fallback to single image for backward compatibility
              allImages.push({ imageId: content.image, language: lang });
            }
          });
          
          popupHTML += `<div style="border-top: 1px solid var(--border-default); margin-top: 12px; padding-top: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <strong style="font-size: var(--text-xs); color: var(--text-heading);">Images:</strong>
            </div>
            <div class="marker-images-container" data-marker-id="${marker.markerId}" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(60px, 1fr)); gap: 8px; margin-bottom: 8px;">
              ${allImages.map((img, idx) => `
                <div class="marker-image-item" data-image-id="${img.imageId}" data-language="${img.language}" style="position: relative; aspect-ratio: 1; border: 1px solid var(--border-default); border-radius: var(--radius-sm); overflow: hidden; background: var(--surface-page); cursor: pointer;">
                  <img src="" data-image-id="${img.imageId}" style="width: 100%; height: 100%; object-fit: cover; display: none;" alt="Image ${idx + 1}" />
                  <div class="image-loading" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: var(--text-xs); color: var(--text-subtle);">Loading...</div>
                  <button class="remove-image-btn" data-image-id="${img.imageId}" data-language="${img.language}" style="position: absolute; top: 2px; right: 2px; background: var(--red-500); color: white; border: none; border-radius: var(--radius-xs, 3px); width: 18px; height: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: var(--text-xs); line-height: 1;">×</button>
                </div>
              `).join('')}
              <button class="add-image-btn" data-marker-id="${marker.markerId}" style="aspect-ratio: 1; border: 2px dashed var(--border-strong); border-radius: var(--radius-sm); background: var(--surface-page); cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-muted); font-size: 24px; transition: all 0.2s;">+</button>
            </div>
          </div>`;
        } else {
          popupHTML += `<div style="margin-top: 24px; padding: 12px 8px; color: var(--text-subtle); font-size: var(--text-xs); text-align: center;">
            No languages configured. Configure languages in account settings to add content.
          </div>`;
        }
      } else {
        // Public mode: PeekCard-style popup
        const title = getMarkerTitle(marker, currentLanguage);
        const catColor = categoryToCssColor(marker.categories?.[0] ?? '');
        const catLabel = marker.categories?.[0] ?? '';
        if (catLabel) {
          popupHTML += `<div style="display:inline-flex;align-items:center;gap:5px;padding:2px 8px;border-radius:999px;background:${catColor};font-size:9px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#fff;margin-bottom:6px;">
            <span style="width:5px;height:5px;border-radius:50%;background:#fff;flex-shrink:0;"></span>${catLabel}
          </div>`;
        }
        if (title && title !== marker.markerId) {
          popupHTML += `<div style="font-size:var(--text-base);font-weight:700;color:var(--text-heading);line-height:1.25;letter-spacing:-0.01em;">${title}</div>`;
        }
        popupHTML += `<div style="display:flex;align-items:center;gap:4px;margin-top:8px;font-size:10px;font-weight:600;color:${catColor};">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6M14 10l7-7M21 21H3V3"/></svg>
          Tap to open
        </div>`;
      }
      
      popupContent.innerHTML = popupHTML;
      popupContent.style.position = 'relative';
      popupContent.style.minHeight = isEditor ? '100px' : 'auto';
      
      // Add copy functionality and tab switching for editor mode
      if (isEditor) {
        const copyIcon = popupContent.querySelector('.copy-icon');
        if (copyIcon) {
          copyIcon.addEventListener('click', async (e) => {
            e.stopPropagation();
            const markerId = (copyIcon as HTMLElement).getAttribute('data-marker-id');
            if (markerId) {
              try {
                await navigator.clipboard.writeText(markerId);
                // Visual feedback - change icon temporarily
                const originalHTML = copyIcon.innerHTML;
                copyIcon.innerHTML = '<path d="M20 6L9 17l-5-5"></path>';
                (copyIcon as HTMLElement).style.color = 'var(--green-500)';
                setTimeout(() => {
                  copyIcon.innerHTML = originalHTML;
                  (copyIcon as HTMLElement).style.color = 'var(--text-muted)';
                }, 1000);
              } catch (err) {
                log.error('Failed to copy marker ID:', err);
              }
            }
          });
          // Add hover effect
          copyIcon.addEventListener('mouseenter', () => {
            (copyIcon as HTMLElement).style.color = 'var(--brand-primary)';
          });
          copyIcon.addEventListener('mouseleave', () => {
            (copyIcon as HTMLElement).style.color = 'var(--text-muted)';
          });
        }
        
        // Tab switching functionality
        const tabButtons = popupContent.querySelectorAll('.popup-tab-btn');
        const tabContents = popupContent.querySelectorAll('.popup-tab-content');
        
        tabButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const lang = (btn as HTMLElement).getAttribute('data-lang');
            const popupId = (btn as HTMLElement).getAttribute('data-popup');
            
            if (lang && popupId) {
              // Update active tab button
              const allBtns = popupContent.querySelectorAll(`[data-popup="${popupId}"]`);
              allBtns.forEach((b) => {
                (b as HTMLElement).style.background = 'transparent';
                (b as HTMLElement).style.borderBottomColor = 'transparent';
              });
              (btn as HTMLElement).style.background = 'var(--surface-page)';
              (btn as HTMLElement).style.borderBottomColor = 'var(--brand-primary)';

              // Show selected tab content
              tabContents.forEach((content) => {
                const contentLang = (content as HTMLElement).getAttribute('data-lang');
                (content as HTMLElement).style.display = contentLang === lang ? 'block' : 'none';
              });
            }
          });
          
          // Add hover effect to tabs
          btn.addEventListener('mouseenter', () => {
            if ((btn as HTMLElement).style.background !== 'var(--surface-page)') {
              (btn as HTMLElement).style.background = 'var(--surface-sunken)';
            }
          });
          btn.addEventListener('mouseleave', () => {
            if ((btn as HTMLElement).style.background !== 'var(--surface-page)') {
              (btn as HTMLElement).style.background = 'transparent';
            }
          });
        });
        
        // Image management functionality
        const setupImageManagement = async () => {
          // Helper function to handle image upload and linking
          const handleImageUpload = async (file: File, markerId: string, showUpdateCue: boolean = false) => {
            // Prevent multiple simultaneous uploads
            if (isUploadingImageRef.current) {
              return;
            }
            
            isUploadingImageRef.current = true;
            
            // Get current active language tab
            const activeTab = popupContent.querySelector('.popup-tab-content[style*="display: block"]') as HTMLElement;
            const language = activeTab?.getAttribute('data-lang') || accountLanguages[0] || 'en';
            
            // Show visual cue on + button if requested
            const addImageBtn = popupContent.querySelector('.add-image-btn') as HTMLElement;
            let successIndicator: HTMLElement | null = null;
            
            if (showUpdateCue && addImageBtn) {
              addImageBtn.style.position = 'relative';
              successIndicator = document.createElement('span');
              successIndicator.innerHTML = `
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="22" y1="12" x2="2" y2="12"></line>
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
                  <line x1="6" y1="16" x2="6.01" y2="16"></line>
                  <line x1="10" y1="16" x2="10.01" y2="16"></line>
                </svg>
              `;
              successIndicator.style.cssText = `
                position: absolute;
                top: -2px;
                right: -2px;
                background: var(--red-50);
                color: var(--danger);
                border-radius: var(--radius-sm);
                width: 18px;
                height: 18px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 4px color-mix(in srgb, var(--red-500) 20%, transparent);
                animation: fadeInScale 0.3s ease;
                z-index: 10;
              `;
              addImageBtn.appendChild(successIndicator);
            }
            
            try {
              // Set editing flag to prevent popup from closing during update
              isEditingRef.current = true;
              
              // Upload the image
              const uploadedMedia = await mediaService.uploadMedia(file, { type: 'image' });
              
              // Link to marker
              await mediaService.linkMedia(uploadedMedia.mediaId, markerId, 'image', language);
              
              // Delay query invalidation to prevent immediate refetch that would close popup
              // We'll invalidate and refetch after the popup refresh
              
              // Remove visual cue after a delay
              if (successIndicator) {
                setTimeout(() => {
                  successIndicator!.style.animation = 'fadeOutScale 0.3s ease';
                  setTimeout(() => {
                    if (successIndicator && successIndicator.parentElement) {
                      successIndicator.remove();
                    }
                  }, 300);
                }, 2000);
              }
              
              // Clear any existing timeout to debounce multiple uploads
              if (imageUploadTimeoutRef.current) {
                clearTimeout(imageUploadTimeoutRef.current);
              }
              
              // Debounce popup refresh to prevent recursive updates and rate limiting
              imageUploadTimeoutRef.current = setTimeout(() => {
                try {
                  // Only invalidate queries - let React Query handle refetching naturally
                  // This prevents rate limiting from too many rapid refetches
                  queryClient.invalidateQueries({ queryKey: ['markers'] });
                  queryClient.invalidateQueries({ queryKey: ['marker', markerId] });
                  queryClient.invalidateQueries({ queryKey: ['media'] });
                  
                  // Close and reopen popup once to refresh content
                  // Keep editing flag true during this process
                  if (leafletMarker.isPopupOpen() && openPopupMarkerIdRef.current === markerId) {
                    leafletMarker.closePopup();
                    
                    // Reopen after a short delay, keeping editing flag true
                    setTimeout(() => {
                      if (openPopupMarkerIdRef.current === markerId) {
                        leafletMarker.openPopup();
                        
                        // Reset flags after popup reopens and useEffect has had time to run
                        setTimeout(() => {
                          isEditingRef.current = false;
                          isUploadingImageRef.current = false;
                          imageUploadTimeoutRef.current = null;
                        }, 800); // Longer delay to ensure useEffect has run and skipped recreation
                      } else {
                        // Marker ID changed, reset flags
                        isEditingRef.current = false;
                        isUploadingImageRef.current = false;
                        imageUploadTimeoutRef.current = null;
                      }
                    }, 150);
                  } else {
                    // Popup not open or different marker, reset flags
                    isEditingRef.current = false;
                    isUploadingImageRef.current = false;
                    imageUploadTimeoutRef.current = null;
                  }
                } catch (error) {
                  // Reset flags on error
                  isEditingRef.current = false;
                  isUploadingImageRef.current = false;
                  imageUploadTimeoutRef.current = null;
                }
              }, 500); // Debounce delay to batch multiple rapid uploads
            } catch (error: unknown) {
              // Reset flags on error
              isEditingRef.current = false;
              isUploadingImageRef.current = false;
              log.error('Failed to add image:', error);
              // Remove visual cue on error
              if (successIndicator && successIndicator.parentElement) {
                successIndicator.remove();
              }
              
              // Provide more helpful error message for rate limiting
              const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string };
              if (err?.response?.status === 429) {
                alert('Too many requests. Please wait a moment and try again.');
              } else {
                alert(`Failed to add image: ${err?.response?.data?.error || err?.message || 'Unknown error'}`);
              }
            }
          };
          
          // Load images for all image items
          const imageItems = popupContent.querySelectorAll('.marker-image-item');
          imageItems.forEach(async (item) => {
            const imageId = (item as HTMLElement).getAttribute('data-image-id');
            const img = item.querySelector('img') as HTMLImageElement;
            const loadingDiv = item.querySelector('.image-loading') as HTMLElement;
            
            if (imageId && img) {
              try {
                const url = await resolveMediaUrl(imageId);
                if (url) {
                  // Ensure URL is absolute if it's a relative path
                  const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
                  
                  // Fetch image with auth headers and convert to blob URL
                  try {
                    const token = localStorage.getItem('authToken');
                    const response = await fetch(absoluteUrl, {
                      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                    });
                    
                    if (!response.ok) {
                      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
                    }
                    
                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    
                    img.src = blobUrl;
                    img.style.display = 'block';
                    if (loadingDiv) loadingDiv.style.display = 'none';
                    
                    // Clean up blob URL when image is loaded or removed
                    img.onload = () => {
                      // Image loaded successfully
                    };
                    
                    // Add error handler
                    img.onerror = () => {
                      log.error('Image failed to load:', img.src);
                      URL.revokeObjectURL(blobUrl);
                      if (loadingDiv) {
                        loadingDiv.textContent = 'Error';
                        loadingDiv.style.display = 'block';
                      }
                      img.style.display = 'none';
                    };
                  } catch (fetchError) {
                    throw fetchError;
                  }
                } else {
                  if (loadingDiv) loadingDiv.textContent = 'Error';
                }
              } catch (error) {
                log.error('Failed to load image:', error);
                if (loadingDiv) loadingDiv.textContent = 'Error';
              }
            }
          });
          
          // Remove image handlers
          const removeButtons = popupContent.querySelectorAll('.remove-image-btn');
          removeButtons.forEach((btn) => {
            btn.addEventListener('click', async (e) => {
              e.stopPropagation();
              const imageId = (btn as HTMLElement).getAttribute('data-image-id');
              const language = (btn as HTMLElement).getAttribute('data-language');
              const markerId = marker.markerId;
              
              if (imageId && language && markerId) {
                try {
                  await mediaService.unlinkMedia(imageId, markerId, language);
                  // Debounce query invalidation to prevent rate limiting
                  // Use a small delay to batch with other potential invalidations
                  setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: ['markers'] });
                    queryClient.invalidateQueries({ queryKey: ['marker', markerId] });
                  }, 100);
                  // Close and reopen popup to refresh
                  if (leafletMarker.isPopupOpen()) {
                    leafletMarker.closePopup();
                    setTimeout(() => {
                      leafletMarker.openPopup();
                    }, 200);
                  }
                } catch (error: unknown) {
                  log.error('Failed to unlink image:', error);
                  const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string };
                  if (err?.response?.status === 429) {
                    alert('Too many requests. Please wait a moment and try again.');
                  } else {
                    alert(`Failed to remove image: ${err?.response?.data?.error || err?.message || 'Unknown error'}`);
                  }
                }
              }
            });
          });
          
          // Get the + button reference first (used by both container and button handlers)
          const addImageBtn = popupContent.querySelector('.add-image-btn') as HTMLElement;
          
          // Make the entire images container a drop zone (no visual feedback on container)
          // Visual feedback only appears on the + button
          const imagesContainer = popupContent.querySelector('.marker-images-container') as HTMLElement;
          if (imagesContainer && addImageBtn) {
            const markerId = imagesContainer.getAttribute('data-marker-id');
            if (markerId) {
              let isContainerDragging = false;
              
              imagesContainer.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Highlight the + button instead of the container
                if (!isContainerDragging) {
                  isContainerDragging = true;
                  addImageBtn.style.background = 'var(--surface-sunken)';
                  addImageBtn.style.borderColor = 'var(--brand-primary)';
                  addImageBtn.style.color = 'var(--brand-primary)';
                }
              });
              
              imagesContainer.addEventListener('dragleave', (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only remove highlight if we're actually leaving the container
                const rect = imagesContainer.getBoundingClientRect();
                const x = e.clientX;
                const y = e.clientY;
                if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                  isContainerDragging = false;
                  addImageBtn.style.background = 'var(--surface-page)';
                  addImageBtn.style.borderColor = 'var(--border-default)';
                  addImageBtn.style.color = 'var(--text-muted)';
                }
              });
              
              imagesContainer.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                isContainerDragging = false;
                // Reset + button styling
                addImageBtn.style.background = 'var(--surface-page)';
                addImageBtn.style.borderColor = 'var(--border-default)';
                addImageBtn.style.color = 'var(--text-muted)';
                
                const files = e.dataTransfer?.files;
                if (files && files.length > 0) {
                  const file = files[0];
                  // Check if it's an image
                  if (file.type.startsWith('image/')) {
                    await handleImageUpload(file, markerId, true);
                  } else {
                    alert('Please drop an image file');
                  }
                }
              });
            }
          }
          
          // Add image button handler
          if (addImageBtn) {
            const markerId = addImageBtn.getAttribute('data-marker-id');
            if (!markerId) return;
            
            // Click handler for file picker
            addImageBtn.addEventListener('click', async (e) => {
              e.stopPropagation();
              
              // Create a simple file input dialog
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.style.display = 'none';
              
              input.addEventListener('change', async (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  await handleImageUpload(file, markerId, true);
                }
                input.remove();
              });
              
              document.body.appendChild(input);
              input.click();
            });
            
            // Drag and drop handlers
            let isDraggingOver = false;
            
            addImageBtn.addEventListener('dragover', (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isDraggingOver) {
                isDraggingOver = true;
                addImageBtn.style.background = 'var(--surface-sunken)';
                addImageBtn.style.borderColor = 'var(--brand-primary)';
                addImageBtn.style.color = 'var(--brand-primary)';
              }
            });
            
            addImageBtn.addEventListener('dragleave', (e) => {
              e.preventDefault();
              e.stopPropagation();
              // Only remove highlight if we're actually leaving the button
              const rect = addImageBtn.getBoundingClientRect();
              const x = e.clientX;
              const y = e.clientY;
              if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                isDraggingOver = false;
                addImageBtn.style.background = 'var(--surface-page)';
                addImageBtn.style.borderColor = 'var(--border-default)';
                addImageBtn.style.color = 'var(--text-muted)';
              }
            });
            
            addImageBtn.addEventListener('drop', async (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              isDraggingOver = false;
              addImageBtn.style.background = 'var(--surface-page)';
              addImageBtn.style.borderColor = 'var(--border-default)';
              addImageBtn.style.color = 'var(--text-muted)';
              
              const files = e.dataTransfer?.files;
              if (files && files.length > 0) {
                const file = files[0];
                // Check if it's an image
                if (file.type.startsWith('image/')) {
                  await handleImageUpload(file, markerId, true);
                } else {
                  alert('Please drop an image file');
                }
              }
            });
          }
        };
        
        // Inline editing functionality
        const setupInlineEditing = (element: Element) => {
          const handleClick = (e: Event) => {
            e.stopPropagation();
            const markerId = (element as HTMLElement).getAttribute('data-marker-id');
            const lang = (element as HTMLElement).getAttribute('data-lang');
            const field = (element as HTMLElement).getAttribute('data-field');
            const contentId = (element as HTMLElement).getAttribute('data-content-id');
            const currentValue = element.textContent || '';
            const displayValue = currentValue.startsWith('(click to add') ? '' : currentValue;
            
            if (!markerId || !lang || !field) return;
            
            // Mark that we're editing to prevent popup from closing
            isEditingRef.current = true;
            
            // Create input/textarea element
            const isLongField = field === 'description' || field === 'plusinfo';
            const input = document.createElement(isLongField ? 'textarea' : 'input') as HTMLInputElement | HTMLTextAreaElement;
            if (!isLongField) {
              (input as HTMLInputElement).type = 'text';
            }
            input.value = displayValue;
            input.style.cssText = `
              width: 100%;
              font-size: var(--text-sm);
              color: var(--text-heading);
              padding: var(--space-1) 6px;
              border: 2px solid var(--brand-primary);
              border-radius: var(--radius-sm);
              background: var(--surface-card);
              font-family: inherit;
              resize: ${isLongField ? 'vertical' : 'none'};
              min-height: ${isLongField ? '60px' : 'auto'};
              max-height: ${isLongField ? '200px' : 'auto'};
            `;
            
            // Store original element for restoration
            const originalElement = element.cloneNode(true) as HTMLElement;
            
            // Replace element with input
            const parent = element.parentElement;
            if (parent) {
              parent.replaceChild(input, element);
              input.focus();
              if (!isLongField) {
                (input as HTMLInputElement).select();
              }
              
              // Save on blur or Enter (for single-line fields)
              const saveValue = async () => {
                const newValue = input.value.trim();
                
                // Mark that we're editing to prevent marker recreation
                isEditingRef.current = true;
                
                try {
                  log.debug('Saving content field:', { markerId, lang, field });
                  await contentService.updateContentField(markerId, lang, field as any, newValue);
                  log.debug('Save successful');
                  
                  // Update the popup content in place without closing it
                  // Replace input with updated display element
                  const displayDiv = document.createElement('div');
                  displayDiv.className = 'editable-content';
                  displayDiv.setAttribute('data-marker-id', markerId);
                  displayDiv.setAttribute('data-lang', lang);
                  displayDiv.setAttribute('data-field', field);
                  if (contentId) {
                    displayDiv.setAttribute('data-content-id', contentId);
                  }
                  
                  // Apply original styles
                  const originalStyle = originalElement.getAttribute('style') || '';
                  displayDiv.style.cssText = originalStyle;
                  
                  displayDiv.textContent = newValue || `(click to add ${field})`;
                  if (!newValue) {
                    displayDiv.style.color = 'var(--text-subtle)';
                    displayDiv.style.fontStyle = 'italic';
                  } else {
                    displayDiv.style.color = '';
                    displayDiv.style.fontStyle = '';
                  }
                  
                  parent.replaceChild(displayDiv, input);
                  
                  // Show visual success indicator (hard drive icon)
                  displayDiv.style.position = 'relative';
                  const successIndicator = document.createElement('span');
                  successIndicator.innerHTML = `
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="22" y1="12" x2="2" y2="12"></line>
                      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
                      <line x1="6" y1="16" x2="6.01" y2="16"></line>
                      <line x1="10" y1="16" x2="10.01" y2="16"></line>
                    </svg>
                  `;
                  successIndicator.style.cssText = `
                    position: absolute;
                    top: -2px;
                    right: -2px;
                    background: var(--red-50);
                    color: var(--danger);
                    border-radius: var(--radius-sm);
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 4px color-mix(in srgb, var(--red-500) 20%, transparent);
                    animation: fadeInScale 0.3s ease;
                  `;
                  displayDiv.appendChild(successIndicator);
                  
                  // Remove indicator after 2 seconds
                  setTimeout(() => {
                    successIndicator.style.animation = 'fadeOutScale 0.3s ease';
                    setTimeout(() => {
                      if (successIndicator.parentElement) {
                        successIndicator.remove();
                      }
                    }, 300);
                  }, 2000);
                  
                  // Re-attach event listeners
                  setupInlineEditing(displayDiv);
                  
                  // Update React Query cache optimistically to keep other components in sync
                  // This won't trigger a refetch because we're using setQueryData
                  queryClient.setQueryData(['markers'], (oldData: MarkerType[] | undefined) => {
                    if (!oldData) return oldData;
                    return oldData.map((m) => {
                      if (m.markerId === markerId) {
                        const updatedMarker = { ...m };
                        if (!updatedMarker.content) {
                          updatedMarker.content = {};
                        }
                        if (!updatedMarker.content[lang]) {
                          updatedMarker.content[lang] = {
                            title: '',
                            description: '',
                            summary: '',
                            plusInfo: '',
                            youtube: ''
                          };
                        }
                        updatedMarker.content[lang] = {
                          ...updatedMarker.content[lang],
                          [field === 'plusinfo' ? 'plusInfo' : field]: newValue
                        };
                        return updatedMarker;
                      }
                      return m;
                    });
                  });
                  
                  // Reset editing flag after a delay to prevent marker recreation
                  // This ensures the popup stays open even if cache update triggers useEffect
                  setTimeout(() => {
                    isEditingRef.current = false;
                  }, 500);
                  
                  // Mark queries as stale for background sync, but don't refetch immediately
                  // The background polling (5 seconds) will naturally sync the changes
                  queryClient.invalidateQueries({ 
                    queryKey: ['markers'],
                    refetchType: 'none' // Don't trigger immediate refetch
                  });
                  queryClient.invalidateQueries({ 
                    queryKey: ['marker', markerId],
                    refetchType: 'none'
                  });
                  queryClient.invalidateQueries({ 
                    queryKey: ['content', markerId, lang],
                    refetchType: 'none'
                  });
                } catch (error: unknown) {
                  log.error('Failed to update content field:', error);
                  const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string };
                  log.error('Error details:', {
                    message: err?.message,
                    response: err?.response?.data,
                    status: err?.response?.status,
                    markerId,
                    lang,
                    field,
                    newValue
                  });
                  isEditingRef.current = false;
                  
                  // Show error message to user
                  const errorMsg = document.createElement('div');
                  errorMsg.textContent = `Failed to save: ${err?.response?.data?.error || err?.message || 'Unknown error'}`;
                  errorMsg.style.cssText = `
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: var(--red-500);
                    color: white;
                    padding: 12px 16px;
                    border-radius: var(--radius-lg);
                    box-shadow: var(--shadow-md);
                    z-index: 10000;
                    font-size: var(--text-sm);
                    max-width: 300px;
                  `;
                  document.body.appendChild(errorMsg);
                  setTimeout(() => {
                    errorMsg.remove();
                  }, 5000);
                  
                  // Restore original element on error
                  parent.replaceChild(originalElement, input);
                  setupInlineEditing(originalElement);
                }
              };
              
              input.addEventListener('blur', saveValue);
              if (!isLongField) {
                input.addEventListener('keydown', (e) => {
                  const keyEvent = e as KeyboardEvent;
                  if (keyEvent.key === 'Enter' && !keyEvent.shiftKey) {
                    keyEvent.preventDefault();
                    input.blur();
                  }
                  if (keyEvent.key === 'Escape') {
                    isEditingRef.current = false;
                    parent.replaceChild(originalElement, input);
                    setupInlineEditing(originalElement);
                  }
                });
              } else {
                input.addEventListener('keydown', (e) => {
                  const keyEvent = e as KeyboardEvent;
                  if (keyEvent.key === 'Escape') {
                    isEditingRef.current = false;
                    parent.replaceChild(originalElement, input);
                    setupInlineEditing(originalElement);
                  }
                });
              }
            }
          };
          
          element.addEventListener('click', handleClick);
          
          // Add hover effect
          element.addEventListener('mouseenter', () => {
            (element as HTMLElement).style.backgroundColor = 'var(--surface-page)';
          });
          element.addEventListener('mouseleave', () => {
            (element as HTMLElement).style.backgroundColor = 'transparent';
          });
        };
        
        const editableElements = popupContent.querySelectorAll('.editable-content');
        editableElements.forEach(setupInlineEditing);
        
        // Setup image management
        setupImageManagement();
      }
      
      leafletMarker.bindPopup(popupContent, {
        maxWidth: isEditor ? 400 : 300,
        minWidth: isEditor ? 300 : 200,
        maxHeight: isEditor ? 500 : 300,
      });
      
      // Store marker reference for reopening popup
      (leafletMarker as any).markerId = marker.markerId;
      
      // Track when this popup opens/closes
      leafletMarker.on('popupopen', () => {
        openPopupMarkerIdRef.current = marker.markerId;
      });
      
      leafletMarker.on('popupclose', () => {
        // Only clear if this is the currently tracked popup
        if (openPopupMarkerIdRef.current === marker.markerId) {
          openPopupMarkerIdRef.current = null;
        }
      });
      
      markersLayer.addLayer(leafletMarker);
    });

    map.addLayer(markersLayer);

    // Reopen popup if it was open before the update
    if (openPopupMarkerIdRef.current) {
      // Use setTimeout to ensure markers are fully added to the map
      setTimeout(() => {
        markersLayer.eachLayer((layer: L.Layer & { markerId?: string; openPopup?: () => void }) => {
          if (layer.markerId === openPopupMarkerIdRef.current) {
            layer.openPopup?.();
            openPopupMarkerIdRef.current = null; // Reset after reopening
          }
        });
      }, 50);
    }

    return () => {
      // Only remove the layer if it's the one we created in this effect run
      // Check if the current layer is the same as the one we created
      if (markersLayerRef.current && markersLayerRef.current === markersLayer) {
        map.removeLayer(markersLayerRef.current);
        // Clear the ref only if we're removing the layer we created
        if (markersLayerRef.current === markersLayer) {
          markersLayerRef.current = null;
        }
      }
    };
  }, [map, markers, currentLanguage, setSelectedMarker, isEditor, queryClient, accountLanguages]);

  return null;
}
